import { AfterViewInit, Component, Input, OnChanges, OnInit } from '@angular/core';
import { AssetData, Item } from '../../../models/assetData.model';
import { CardData } from './donut-card/donut-card.component';
import { DashboardService } from '../../../services/dashboard.service';
import { CategoryData } from '../../../models/category-data.model';
import { SubstationTemplateStat } from '../../../models/substation-template-stat.model';

@Component({
  selector: 'lib-total-asset-breakdown',
  standalone: false,
  templateUrl: './total-asset-breakdown.component.html',
  styleUrl: './total-asset-breakdown.component.scss',
})
export class TotalAssetBreakdownComponent implements OnChanges {
  @Input() poleData: AssetData = new AssetData();
  @Input() customersData: AssetData = new AssetData();
  @Input() buildingsData: AssetData = new AssetData();
  @Input() distributionSubstationsData: AssetData = new AssetData();
  @Input() tsData: AssetData = new AssetData();
  @Input() substationData: AssetData = new AssetData();
  @Input() devicesData: AssetData = new AssetData();
  @Input() sectionDefinitionData: AssetData = new AssetData();
  @Input() deviceData: AssetData = new AssetData();
  @Input() f11Data: AssetData = new AssetData();
  @Input() f33Data: AssetData = new AssetData();
  @Input() feedersData: AssetData = new AssetData();
  @Input() cimFeedersData: AssetData = new AssetData();
  @Input() mainFeederData: AssetData = new AssetData();
  @Input() primaryFeederData: AssetData = new AssetData();
  @Input() secondaryFeederData: AssetData = new AssetData();
  @Input() equipmentData: AssetData = new AssetData();
  @Input() substationTemplateStats: SubstationTemplateStat[] = [];
  @Input() hasCimDataIntegration: boolean = false;
  @Input() areaIds: number[] = [];
  @Input() dateFrom: any;
  @Input() dateTo: any;

  networkStructureData: AssetData = new AssetData();

  selectedDetailsType: 'customer' | 'pole' | 'network structure' | null = null;
  customerBreakdownData: CardData[] = [];
  networkStructureBreakdownData: CardData[] = [];
  poleBreakdownData: CardData[] = [];

  constructor(private dashboardService: DashboardService) {}

  ngOnChanges(): void {
    this.getNetworkStructureData();
  }

  private NETWORK_STRUCTURE_COLOR_MAP: Record<string, string> = {
    'Transmission Station': '#C03438',
    'Injection Substation': '#ED6300',
    'Distribution Substation': '#60951B',
  };

  private readonly HV_VOLTAGE_CLASS = 'HV';
  private readonly MV_VOLTAGE_CLASS = 'MV';
  private readonly LV_VOLTAGE_CLASS = 'LV';

  private sumTemplateStats(voltageClass: string, field: 'count' | 'previousMonthTotal' | 'countIncrease'): number {
    return (this.substationTemplateStats ?? [])
      .filter((t) => t.voltageClass === voltageClass)
      .reduce((sum, t) => sum + (t[field] || 0), 0);
  }

  get tsCount(): number {
    return this.hasCimDataIntegration ? this.sumTemplateStats(this.HV_VOLTAGE_CLASS, 'count') : this.tsData.totalCount;
  }
  get injectionSubstationCount(): number {
    return this.hasCimDataIntegration ? this.sumTemplateStats(this.MV_VOLTAGE_CLASS, 'count') : this.substationData.totalCount;
  }
  get distributionSubstationCount(): number {
    return this.hasCimDataIntegration ? this.sumTemplateStats(this.LV_VOLTAGE_CLASS, 'count') : this.distributionSubstationsData.totalCount;
  }
  get tsPreviousMonthTotal(): number {
    return this.hasCimDataIntegration ? this.sumTemplateStats(this.HV_VOLTAGE_CLASS, 'previousMonthTotal') : this.tsData.previousMonthTotal;
  }
  get injectionSubstationPreviousMonthTotal(): number {
    return this.hasCimDataIntegration ? this.sumTemplateStats(this.MV_VOLTAGE_CLASS, 'previousMonthTotal') : this.substationData.previousMonthTotal;
  }
  get distributionSubstationPreviousMonthTotal(): number {
    return this.hasCimDataIntegration ? this.sumTemplateStats(this.LV_VOLTAGE_CLASS, 'previousMonthTotal') : this.distributionSubstationsData.previousMonthTotal;
  }
  get tsCountIncrease(): number {
    return this.hasCimDataIntegration ? this.sumTemplateStats(this.HV_VOLTAGE_CLASS, 'countIncrease') : this.tsData.countIncrease;
  }
  get injectionSubstationCountIncrease(): number {
    return this.hasCimDataIntegration ? this.sumTemplateStats(this.MV_VOLTAGE_CLASS, 'countIncrease') : this.substationData.countIncrease;
  }
  get distributionSubstationCountIncrease(): number {
    return this.hasCimDataIntegration ? this.sumTemplateStats(this.LV_VOLTAGE_CLASS, 'countIncrease') : this.distributionSubstationsData.countIncrease;
  }

  private getNetworkStructureData() {
    const ts = this.tsCount;
    const injection = this.injectionSubstationCount;
    const distribution = this.distributionSubstationCount;
    const total = ts + injection + distribution;
    const previousMonthTotal = this.tsPreviousMonthTotal + this.injectionSubstationPreviousMonthTotal + this.distributionSubstationPreviousMonthTotal;
    const countIncrease = this.tsCountIncrease + this.injectionSubstationCountIncrease + this.distributionSubstationCountIncrease;

    this.networkStructureData = new AssetData({
      assetName: "Network Structure",
      totalCount: total,
      previousMonthTotal: previousMonthTotal,
      countIncrease: countIncrease,
    })

    const countByKey: Record<string, number> = {
      'Transmission Station': ts,
      'Injection Substation': injection,
      'Distribution Substation': distribution,
    };

    this.networkStructureData.items = ['Transmission Station', 'Injection Substation', 'Distribution Substation'].map((key) =>
      new Item(({
        key,
        translationKey: key,
        value: countByKey[key],
        share: 0,
        isNew: undefined
      }))
    )
  }

  get assetAnalysisCategories(): CategoryData[] {
    const categories: CategoryData[] = [
      { name: 'CUSTOMERS.PLURAL', count: this.customersData.totalCount, color: '#6aad63' },
      { name: 'COMMON.BUILDING.PLURAL', count: this.buildingsData.totalCount, color: '#bcbc03' },
      { name: 'ADVANCED_MAP.LEGEND.POLES', count: this.poleSum, color: '#6c2e40' },
    ];

    if (this.hasCimDataIntegration) {
      categories.push(
        { name: 'DATA_MODEL_CUSTOMIZATION.CATEGORIES.NETWORK_STRUCTURE', count: this.networkStructureData.totalCount, color: '#60951b' },
        { name: 'NETWORK_ASSET.NODE.LABEL.Feeders', count: this.feedersOverviewData.totalCount, color: '#083F72' },
        { name: 'NETWORK_ASSET.NODE.LABEL.Devices', count: this.devicesOverviewData.totalCount, color: '#6A328B' }
      );
    } else {
      categories.push(
        { name: 'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.DISTRIBUTION_SUBSTATIONS', count: this.distributionSubstationCount, color: '#60951b' },
        { name: 'NETWORK_ASSET.NODE.LABEL.Feeders', count: this.feedersOverviewData.totalCount, color: '#083F72' },
        { name: 'NETWORK_ASSET.NODE.LABEL.Devices', count: this.devicesOverviewData.totalCount, color: '#6A328B' }
      );
    }

    return categories;
  }

  getAssetSum(): number {
    return this.assetAnalysisCategories.reduce((sum, c) => sum + (c.count || 0), 0);
  }

  get poleSum(): number {
    return (
      this.poleData?.items?.reduce((sum, item) => sum + item.value, 0) ?? 0
    );
  }

  get hvPolesCount(): number {
    return this.getPoleValueByKey('hvPoles');
  }

  get mvPolesCount(): number {
    return this.getPoleValueByKey('mvPoles');
  }

  get ltPolesCount(): number {
    return this.getPoleValueByKey('ltPoles');
  }

  get poleCount(): number {
    return this.getDTValueByKey('Pole');
  }

  get groundCount(): number {
    return this.getDTValueByKey('Ground');
  }

  private getDTValueByKey(key: string): number {
    return (
      this.distributionSubstationsData?.items?.find((item) => item.key === key)
        ?.value ?? 0
    );
  }

  private getPoleValueByKey(key: string): number {
    return this.poleData?.items?.find((item) => item.key === key)?.value ?? 0;
  }

  POLE_COLOR_MAP: Record<string, string> = {
    // Pole card
    'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.HV_POLES': '#6c2e40',
    'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.MV_POLES': '#b4506e',
    'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.LV_POLES': '#d987a0',

    // Pole breakdown
    'COMMON.POLE.TYPE.CONCRETE': '#B4506E',
    'COMMON.POLE.TYPE.STEEL': '#6C2E40',
    'COMMON.POLE.TYPE.WOODEN': '#D987A0',
  };

  private generateCustomerColor(index: number): string {
    return `hsl(116, 41%, ${25 + index * 10}%)`;
  }

  get feedersOverviewData(): AssetData {
    return this.hasCimDataIntegration ? this.cimFeedersData : this.feedersData;
  }
  get devicesOverviewData(): AssetData {
    return this.hasCimDataIntegration ? this.equipmentData : this.distributionSubstationsData;
  }

  get feederCategories(): CategoryData[] {
    if (this.hasCimDataIntegration) {
      return [
        {
          name: 'Main',
          count: this.mainFeederData.totalCount,
          color: '#083F72'
        },
        {
          name: 'Primary',
          count: this.primaryFeederData.totalCount,
          color: '#94B2CD'
        },
        {
          name: 'Secondary',
          count: this.secondaryFeederData.totalCount,
          color: '#C8DAEB'
        },
      ];
    }

    return [
      {
        name: 'Main',
        count: this.f33Data.totalCount,
        color: '#083F72'
      },
      {
        name: 'Primary',
        count: this.f11Data.totalCount,
        color: '#94B2CD'
      },
    ];
  }

  get networkStructureCategories(): CategoryData[] {
    if (this.hasCimDataIntegration) {
      return (this.substationTemplateStats ?? []).map((t) => ({
        name: t.templateName,
        count: t.count,
        color: t.color || '#60951B',
      }));
    }

    return [
      {
        name: 'TS',
        count: this.tsCount,
        color: "#C03438"
      },
      {
        name: 'INJ. SS',
        count: this.injectionSubstationCount,
        color: "#ED6300"
      },
      {
        name: 'Distribution SS',
        count: this.distributionSubstationCount,
        color: "#60951B"
      },
    ]
  }

  get customerCategories(): CategoryData[] {
    if (!this.customersData?.items) return [];

    const grouped = this.customersData.items.reduce((acc, item) => {
      const key = item.key.toUpperCase();
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key] += item.value;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(grouped).map(([name, count], index) => ({
      name,
      count,
      color: this.generateCustomerColor(index),
    }));
  }

  get deviceCategories(): CategoryData[] {
    if (this.hasCimDataIntegration) {
      return [
        {
          name: 'AI.DEVICES',
          count: this.equipmentData.totalCount,
          color: '#6A328B'
        },
      ];
    }

    return [
      {
        name: 'Transformers',
        count: this.distributionSubstationsData.totalCount,
        color: '#6A328B'
      },
    ]
  }

  get poleCategories(): CategoryData[] {
    return [
      {
        name: 'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.HV_POLES',
        count: this.hvPolesCount,
        color:
          this.POLE_COLOR_MAP[
            'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.HV_POLES'
          ],
      },
      {
        name: 'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.MV_POLES',
        count: this.mvPolesCount,
        color:
          this.POLE_COLOR_MAP[
            'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.MV_POLES'
          ],
      },
      {
        name: 'AI.LV_POLES',
        count: this.ltPolesCount,
        color:
          this.POLE_COLOR_MAP[
            'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.LV_POLES'
          ],
      },
    ];
  }

  openDetailsModal(type: 'customer' | 'pole' | 'network structure') {
    this.selectedDetailsType = type;

    document.body.classList.add('modal-open');

    if (type == 'customer') {
      this.createCustomerBreakdownByIsNew();
    } else if (type == 'network structure') {
      this.createNetworkStructureBreakdown();
    }
     else {
      this.createPoleBreakdown();
    }
  }

  private createNetworkStructureBreakdown() {
    if (this.hasCimDataIntegration) {
      this.networkStructureBreakdownData = (this.substationTemplateStats ?? []).map((t) =>
        new CardData(t.templateName, t.count, [
          { name: t.templateName, count: t.count, color: t.color || '#60951B' },
        ])
      );
      return;
    }

    if (!this.networkStructureData?.items) return;

    const groupByName = (items: typeof this.networkStructureData.items) => {
      const grouped = items.reduce((acc, item) => {
        const key = item.key;
        acc[key] = (acc[key] || 0) + item.value;
        return acc;
      }, {} as Record<string, number>);

      return Object.entries(grouped).map(([name, count], index) => ({
        name,
        count,
        color: this.NETWORK_STRUCTURE_COLOR_MAP[name] || '#000000',
      }));
    };

    const tsItems = this.networkStructureData.items.filter(i => i.key === 'Transmission Station');
    const ssItems = this.networkStructureData.items.filter(i => i.key === 'Injection Substation');
    const dtItems = this.networkStructureData.items.filter(i => i.key === 'Distribution Substation');

    const sumCounts = (cats: CategoryData[]) =>
      cats.reduce((sum, c) => sum + c.count, 0);

    const tsCategories = groupByName(tsItems);
    const ssCategories = groupByName(ssItems);
    const dtCategories = groupByName(dtItems);

    this.networkStructureBreakdownData = [
      new CardData('Transmission Station', sumCounts(tsCategories), tsCategories),
      new CardData(
        'Injection Substation',
        sumCounts(ssCategories),
        ssCategories
      ),
      new CardData(
        'Distribution Substation',
        sumCounts(dtCategories),
        dtCategories
      ),
    ];
  }

  getNetworkStructureCategoryColor(index: number): string {
    return this.networkStructureCategories[index]?.color || '#ccc';
  }

  onCloseDetailsModal() {
    this.selectedDetailsType = null;
    document.body.classList.remove('modal-open');
  }

  get buildingCategories() {
    return this.getCategoriesFromAssetData(this.buildingsData, {
      Inaccessible: '#bcbc03',
      Tagged: '#757500',
    });
  }

  getCategoryShare(category: CategoryData): number {
    const total = this.customerCategories.reduce(
      (sum, cat) => sum + cat.count,
      0
    );
    return total ? (category.count / total) * 100 : 0;
  }

  getCategoriesFromAssetData(
    data: AssetData,
    colorMap: { [key: string]: string }
  ): { name: string; count: number; color: string }[] {
    if (!data?.items?.length) return [];

    return data.items.map((item) => ({
      name: item.translationKey,
      count: item.value,
      color: colorMap[item.key] ?? '#cccccc',
    }));
  }

  private createCustomerBreakdownByIsNew(): void {
    if (!this.customersData?.items) return;

    const groupByName = (items: typeof this.customersData.items) => {
      const grouped = items.reduce((acc, item) => {
        const key = item.key.toUpperCase();
        acc[key] = (acc[key] || 0) + item.value;
        return acc;
      }, {} as Record<string, number>);

      return Object.entries(grouped).map(([name, count], index) => ({
        name,
        count,
        color: this.generateCustomerColor(index),
      }));
    };

    const newItems = this.customersData.items.filter((i) => i.isNew);
    const existingItems = this.customersData.items.filter((i) => !i.isNew);

    const sumCounts = (cats: CategoryData[]) =>
      cats.reduce((sum, c) => sum + c.count, 0);

    const newCategories = groupByName(newItems);
    const existingCategories = groupByName(existingItems);

    this.customerBreakdownData = [
      new CardData('COMMON.NEW', sumCounts(newCategories), newCategories),
      new CardData(
        'COMMON.EXISTING',
        sumCounts(existingCategories),
        existingCategories
      ),
    ];
  }

  private createPoleBreakdown(): void {
    const obj = { areaIds: this.areaIds, dateFrom: this.dateFrom, dateTo: this.dateTo };

    this.dashboardService.getPoleDashboardBreakdownData(obj).subscribe({
      next: (data) => {
        const rawData = data?.body?.data ?? [];
        const cards = rawData.map((item: any) => {
          const categories = item.categories.map((cat: any) => ({
            name: cat.name,
            count: cat.count,
            color: this.POLE_COLOR_MAP[cat.name] || '#000000',
          }));

          return new CardData(item.title, item.totalCount, categories);
        });

        const referenceCategories =
          cards[0]?.categories.map(
            (cat: { name: string; count: number; color: string }) => ({
              name: cat.name,
              count: 0,
              color: cat.color,
            })
          ) ?? [];

        const hvPoleCard = new CardData(
          'MAIN_SIDEBAR.DETAILS_SIDEBAR.ASSET_LIST.HV_POLES',
          0,
          referenceCategories
        );

        this.poleBreakdownData = [...cards, hvPoleCard];
      },
      error: (error) => {
        console.log(error.statusText);
      },
    });
  }

}
